/**
 * BookAppointmentSheet — the ONE shared staff-side booking flow (APT-02).
 * Service → day → slot (each slot names its staffer) → customer details →
 * awaited bookAppointment. Mounted from the Appointments book header; the
 * concierge (QueueConsole), Customers 360 and My Day converge onto this sheet
 * as follow-ups (their in-place flows keep working meanwhile — REQ-089 notes
 * the alias). Booking AWAITS the op; the post-booking confirmation journey is
 * served by the APT-08 comms templates once live — the sheet states that,
 * never fakes a confirmation send.
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, Button, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  bookAppointment, listAppointmentServices, listApptSlots,
  type Appointment, type ApptAttendee, type ApptPartyRef, type ApptServiceRef, type ApptSlot,
} from "./appointments-api";
import { tzContext, tzShortLabel } from "../../lib/datetime";
import { useEventGate } from "./event-gate";
import { useStaff } from "../staff/useStaff";
import { useResources } from "../resources/useResources";

const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(15,18,24,0.45)", zIndex: 240, display: "flex", justifyContent: "flex-end" };
const panel: React.CSSProperties = { width: "min(440px, 100vw)", height: "100%", background: "#fff", display: "flex", flexDirection: "column", boxShadow: "-12px 0 40px rgba(15,18,24,0.18)" };

function nextDays(n: number): { iso: string; label: string }[] {
  const out: { iso: string; label: string }[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    out.push({ iso, label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" }) });
  }
  return out;
}

export default function BookAppointmentSheet({ open, onClose, locationId, prefill, origin, onBooked }: {
  open: boolean; onClose: () => void; locationId?: string | null;
  prefill?: { name?: string; email?: string; phone?: string };
  origin?: string; onBooked?: (a: Appointment) => void;
}) {
  const t = useT();
  const days = useMemo(() => nextDays(7), []);
  const [services, setServices] = useState<ApptServiceRef[] | null>(null);
  const [svcErr, setSvcErr] = useState<string | null>(null);
  const [service, setService] = useState<ApptServiceRef | null>(null);
  const [date, setDate] = useState(days[0].iso);
  const [slots, setSlots] = useState<ApptSlot[] | null>(null);
  const [slotErr, setSlotErr] = useState<string | null>(null);
  const [staffFilter, setStaffFilter] = useState(""); // APT-06: preferred staff — filters the slot grid client-side (each slot names its staffer)
  /* UC2 round-robin (2026-08-13): auto-assign rotates through the service's
   * ELIGIBLE pool (the slot grid is already eligibility-filtered server-side).
   * The rotation pointer persists per service (localStorage) so consecutive
   * bookings genuinely rotate rather than always taking the first free
   * advisor. When on, the grid collapses to unique TIMES; the rotation picks
   * whose slot a time maps to. */
  const [autoAssign, setAutoAssign] = useState(false);
  const [pick, setPick] = useState<ApptSlot | null>(null);
  const [name, setName] = useState(prefill?.name ?? "");
  const [email, setEmail] = useState(prefill?.email ?? "");
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [booked, setBooked] = useState<Appointment | null>(null);
  /* JRNI §6 wire-in (2026-08-05): extra customer attendees, secondary staff and
   * consumed resources on the same booking — all optional, all persisted by the
   * serving path (REQ-130 carries the declared-family shape). */
  const [attendees, setAttendees] = useState<ApptAttendee[]>([]);
  const [addStaff, setAddStaff] = useState<string[]>([]);
  const [resourceIds, setResourceIds] = useState<string[]>([]);
  const { items: resourceItems } = useResources();
  const zoneCtx = useMemo(() => tzContext(), []);
  /* UC6 event-gated eligibility (2026-08-13): a service may be unlocked by
   * check-in at a named event (events.json `unlocks` block). gateFor(name) →
   * null (ungated) or { eventTitle, allowed(customerName) }. Evaluated against
   * the sheet's customer name, live as staff type — and re-checked in canSave
   * so the rule can't be typed around after picking the service. Public
   * surfaces hide gated services entirely via the same rule. */
  const gates = useEventGate();

  useEffect(() => {
    if (!open) return;
    setBooked(null); setErr(null); setPick(null);
    setAttendees([]); setAddStaff([]); setResourceIds([]);
    setName(prefill?.name ?? ""); setEmail(prefill?.email ?? ""); setPhone(prefill?.phone ?? "");
    listAppointmentServices(locationId).then((r) => { setServices(r.services); setSvcErr(null); })
      .catch((e) => setSvcErr(e instanceof Error ? e.message : String(e)));
  }, [open, locationId, prefill?.name, prefill?.email, prefill?.phone]);

  useEffect(() => {
    if (!open || !service) return;
    setSlots(null); setPick(null); setSlotErr(null); setStaffFilter("");
    listApptSlots({ service: service.name, date, locationId })
      .then((r) => setSlots(r.slots)).catch((e) => setSlotErr(e instanceof Error ? e.message : String(e)));
  }, [open, service, date, locationId]);

  const staffOptions = useMemo(() => { const seen = new Map<string, string>(); (slots ?? []).forEach((s) => { if (!seen.has(s.staffId)) seen.set(s.staffId, s.staffName); }); return [...seen.entries()].map(([id, nm]) => ({ id, name: nm })); }, [slots]);
  const rrPick = useMemo(() => {
    if (!autoAssign || !service) return null;
    const byTime = new Map<string, ApptSlot[]>();
    (slots ?? []).forEach((sl) => { const arr = byTime.get(sl.start) ?? []; arr.push(sl); byTime.set(sl.start, arr); });
    let ptr = 0;
    try { ptr = Number(localStorage.getItem("cd:rr:" + service.name) || "0") || 0; } catch { /* no storage */ }
    const order = staffOptions.map((o) => o.id);
    const out = new Map<string, ApptSlot>();
    [...byTime.entries()].forEach(([start, arr], k) => {
      const want = order.length ? order[(ptr + k) % order.length] : null;
      out.set(start, arr.find((x) => x.staffId === want) ?? arr[0]);
    });
    return out;
  }, [autoAssign, service, slots, staffOptions]);
  const shownSlots = autoAssign && rrPick ? [...rrPick.values()].sort((a, b) => a.start.localeCompare(b.start)) : (slots ?? []).filter((s) => !staffFilter || s.staffId === staffFilter);
  /* Whose wall clock (2026-08-07, Pat): slot times are the BRANCH's local time.
     When the chosen staff member's own profile zone differs, say so and give
     their time too — a Chicago-based banker should not have to do the
     arithmetic while a customer is on the line. Matched by name: slot staffIds
     come from the availability op, roster ids from the staff op, and only the
     name is guaranteed to be the same key in both. */
  const { members: rosterMembers } = useStaff();
  const selStaffName = pick?.staffName || staffOptions.find((s) => s.id === staffFilter)?.name || "";
  const selStaffTz = (() => {
    if (!selStaffName) return null;
    const hit = rosterMembers.find((m) => (m.name || "").toLowerCase() === selStaffName.toLowerCase());
    const tz = (hit as { timezone?: string | null } | undefined)?.timezone ?? null;
    return tz && tz !== zoneCtx.zone ? tz : null;
  })();

  if (!open) return null;
  /* A gated pick can INVALIDATE when staff change the customer name afterwards —
   * re-check at save-eligibility time so the rule can't be typed around. */
  const pickedGate = service ? gates.gateFor(service.name) : null;
  const gateBlocked = !!pickedGate && !pickedGate.allowed(name);
  const canSave = !!service && !!pick && name.trim().length > 1 && !saving && !gateBlocked;

  const save = async () => {
    if (!service || !pick) return;
    setSaving(true); setErr(null);
    try {
      if (autoAssign) { try { localStorage.setItem("cd:rr:" + service.name, String((Number(localStorage.getItem("cd:rr:" + service.name) || "0") || 0) + 1)); } catch { /* no storage */ } }
      const a = await bookAppointment({
        service: service.name, serviceId: service.id, date, start: pick.start, end: pick.end,
        staffId: pick.staffId, staffName: pick.staffName, locationId,
        customer: { name: name.trim(), email: email.trim() || undefined, phone: phone.trim() || undefined },
        attendees: attendees.map((x) => ({ name: x.name.trim(), email: x.email?.trim() || undefined })).filter((x) => x.name),
        additionalStaff: staffOptions.filter((s) => addStaff.includes(s.id)).map((s): ApptPartyRef => ({ id: s.id, name: s.name })),
        resources: resourceItems.filter((r) => resourceIds.includes(r.id)).map((r): ApptPartyRef => ({ id: r.id, name: r.name })),
        origin: origin ?? "staff",
      });
      setBooked(a); onBooked?.(a);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 };
  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="appt-book-sheet">
      <div style={panel} role="dialog" aria-label={t("appointments.sheet.bookAnAppointment", "Book an appointment")}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${T.n6}` }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{t("appointments.sheet.bookAnAppointment", "Book an appointment")}</span>
          <Button hierarchy="tertiary" size="sm" onClick={onClose} aria-label={t("appointments.sheet.close", "Close")}><Icon.x size={16} /></Button>
        </div>
        {booked ? (
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: T.green1, fontWeight: 700 }}><Icon.check size={18} />{t("appointments.sheet.booked", "Booked")} · {booked.ref}</div>
            <div style={{ fontSize: 13.5, color: T.n2 }}>{booked.service} · {booked.date} · {booked.start}–{booked.end}{booked.staffName ? ` · ${booked.staffName}` : ""}{booked.additionalStaff?.length ? ` + ${booked.additionalStaff.map((s) => s.name).join(", ")}` : ""}</div>
            {booked.attendees?.length || booked.resources?.length ? (
              <div style={{ fontSize: 12.5, color: T.n2 }}>
                {booked.attendees?.length ? <>{booked.attendees.length + 1} {t("appointments.sheet.attendeesWord", "attendees")}</> : null}
                {booked.attendees?.length && booked.resources?.length ? " · " : ""}
                {booked.resources?.length ? booked.resources.map((r) => r.name).join(", ") : null}
              </div>
            ) : null}
            <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.6 }}>{t("appointments.sheet.confirmationToFollow", "Confirmation to follow — the standard appointment confirmation flow sends the customer their details and manage-booking link.")}</div>
            <Button onClick={onClose}>{t("appointments.sheet.done", "Done")}</Button>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <div style={label}>{t("appointments.sheet.service", "Service")}</div>
              {svcErr ? <div style={{ fontSize: 12.5, color: T.red1 }}>{svcErr}</div> : !services ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.loadingServices", "Loading services…")}</div> : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {services.map((s) => {
                    const gate = gates.gateFor(s.name);
                    /* Selection stays open while the name is still blank — the sheet
                     * asks for the customer AFTER the service, so an empty-name hard
                     * lock made the gated service unpickable for everyone (verifier,
                     * 2026-08-13). Enforcement lives in the save-time guard + amber
                     * notice once a name exists; the lock here is the early signal
                     * when a known non-attendee's name is already on the sheet. */
                    const locked = !!gate && name.trim().length > 1 && !gate.allowed(name);
                    return (
                    /* ui-raw: service choice tile with a gate hint — ChoiceTile/SelectableTile's pattern (group 10) */
                    <button key={s.id} onClick={() => { if (!locked) setService(s); }} data-testid={`appt-sheet-svc-${s.id}`}
                      title={gate ? (locked ? gates.lockedHint(gate) : name.trim().length > 1 ? gates.unlockedHint(gate) : gates.reservedHint(gate)) : undefined}
                      style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${service?.id === s.id ? T.p1 : T.n5}`, background: service?.id === s.id ? "#F4F2FF" : locked ? T.n6 : "#fff", fontSize: 12.5, fontWeight: 600, cursor: locked ? "not-allowed" : "pointer", color: locked ? T.soft : T.n1 }}>
                      {locked ? <Icon.lock size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} /> : null}{s.name} <span style={{ color: T.soft, fontWeight: 500 }}>· {s.duration}m</span>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
            {service ? (
              <div>
                <div style={label}>{t("appointments.sheet.day", "Day")}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {days.map((d) => (
                    /* ui-raw: date chip row — DayStrip's pattern (group 9) */
                    <button key={d.iso} onClick={() => setDate(d.iso)} style={{ padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${date === d.iso ? T.p1 : T.n5}`, background: date === d.iso ? "#F4F2FF" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{d.label}</button>
                  ))}
                </div>
              </div>
            ) : null}
            {service ? (
              <div>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                  <div style={label}>{t("appointments.sheet.time", "Time")}</div>
                  {staffOptions.length > 1 ? (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.soft, cursor: "pointer" }} title={t("appointments.sheet.autoAssignHint", "Rotates bookings through the service's eligible staff")}>
                      <input type="checkbox" checked={autoAssign} onChange={(e) => { setAutoAssign(e.target.checked); setStaffFilter(""); setPick(null); }} data-testid="appt-sheet-autoassign" />
                      {t("appointments.sheet.autoAssign", "Auto-assign (round-robin)")}
                    </label>
                  ) : null}
                  {staffOptions.length > 1 && !autoAssign ? (
                    <select value={staffFilter} onChange={(e) => { setStaffFilter(e.target.value); setPick(null); }} data-testid="appt-sheet-staff-filter"
                      style={{ padding: "4px 8px", borderRadius: 7, border: `1px solid ${T.n5}`, fontSize: 11.5, background: "#fff", color: T.n2, maxWidth: 180 }}>
                      <option value="">{t("appointments.sheet.anyStaff", "Any staff")}</option>
                      {staffOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  ) : null}
                </div>
                <div style={{ fontSize: 11.5, color: T.soft, margin: "2px 0 8px", lineHeight: 1.5 }} data-testid="appt-sheet-tz-note">
                  {t("appointments.sheet.timesLocalToBranch", "Times are the branch's local time")} ({zoneCtx.label}){zoneCtx.differs ? <> · {t("appointments.sheet.deviceZoneDiffers", "your device is on")} {zoneCtx.browserLabel}</> : null}
                  {selStaffTz ? <> · {selStaffName} {t("appointments.sheet.staffZoneDiffers", "works in")} {selStaffTz} ({tzShortLabel(selStaffTz)})</> : null}
                </div>
                {slotErr ? <div style={{ fontSize: 12.5, color: T.red1 }}>{slotErr}</div> : !slots ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.loadingSlots", "Loading availability…")}</div> : shownSlots.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.noSlots", "No open times for this day — try another day.")}</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
                    {shownSlots.map((s) => (
                      /* ui-raw: appointment slot button — SlotPicker's pattern (group 9) */
                      <button key={s.start + s.staffId} onClick={() => setPick(s)} data-testid="appt-sheet-slot"
                        style={{ padding: "8px 6px", borderRadius: 8, border: `1.5px solid ${pick === s ? T.p1 : T.n5}`, background: pick === s ? "#F4F2FF" : "#fff", cursor: "pointer" }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{s.start}</div>
                        <div style={{ fontSize: 10.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.staffName}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
            {pick ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={label}>{t("appointments.sheet.customer", "Customer")}</div>
                <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder={t("appointments.sheet.fullName", "Full name")} data-testid="appt-sheet-name" />
                <Input value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)} placeholder={t("appointments.sheet.mobileOptional", "Mobile (optional)")} />
                <Input value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} placeholder={t("appointments.sheet.emailOptional", "Email (optional)")} />
                <div style={{ ...label, marginTop: 8 }}>{t("appointments.sheet.additionalAttendees", "Additional attendees")}</div>
                {attendees.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }} data-testid="appt-sheet-attendee-row">
                    <Input value={a.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAttendees(attendees.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={t("appointments.sheet.attendeeName", "Attendee name")} />
                    <Input value={a.email ?? ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAttendees(attendees.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} placeholder={t("appointments.sheet.emailOptional", "Email (optional)")} />
                    <Button hierarchy="tertiary" size="sm" onClick={() => setAttendees(attendees.filter((_, j) => j !== i))} aria-label={t("appointments.sheet.removeAttendee", "Remove")}><Icon.x size={14} /></Button>
                  </div>
                ))}
                <Btn size="sm" variant="secondary" testid="appt-sheet-add-attendee" style={{ alignSelf: "flex-start" }} onClick={() => setAttendees([...attendees, { name: "" }])}>
                  {t("appointments.sheet.addAttendee", "Add attendee")}
                </Btn>
                {pick && staffOptions.filter((s) => s.id !== pick.staffId).length > 0 ? (
                  <>
                    <div style={{ ...label, marginTop: 8 }}>{t("appointments.sheet.additionalStaffLabel", "Additional staff")}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {staffOptions.filter((s) => s.id !== pick.staffId).map((s) => (
                        /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
                        <button key={s.id} onClick={() => setAddStaff(addStaff.includes(s.id) ? addStaff.filter((x) => x !== s.id) : [...addStaff, s.id])} data-testid="appt-sheet-addstaff"
                          style={{ padding: "6px 10px", borderRadius: 999, border: `1.5px solid ${addStaff.includes(s.id) ? T.p1 : T.n5}`, background: addStaff.includes(s.id) ? "#F4F2FF" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
                {resourceItems.filter((r) => r.status === "active").length > 0 ? (
                  <>
                    <div style={{ ...label, marginTop: 8 }}>{t("appointments.sheet.resourcesLabel", "Resources")}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {resourceItems.filter((r) => r.status === "active").map((r) => (
                        /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
                        <button key={r.id} onClick={() => setResourceIds(resourceIds.includes(r.id) ? resourceIds.filter((x) => x !== r.id) : [...resourceIds, r.id])} data-testid="appt-sheet-resource"
                          style={{ padding: "6px 10px", borderRadius: 999, border: `1.5px solid ${resourceIds.includes(r.id) ? T.p1 : T.n5}`, background: resourceIds.includes(r.id) ? "#F4F2FF" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
            {gateBlocked && pickedGate ? <div style={{ margin: "0 20px", padding: "9px 12px", borderRadius: 8, background: "#FCF1EA", border: "1px solid #F3D4BE", fontSize: 12.5, color: "#A33B00" }}>{gates.lockedHint(pickedGate)}</div> : null}
        {err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
          </div>
        )}
        {!booked ? (
          <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.n6}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={onClose}>{t("appointments.sheet.cancel", "Cancel")}</Button>
            <Button disabled={!canSave} onClick={() => { void save().then(() => { /* toast on success handled by booked state */ }); }} data-testid="appt-sheet-confirm">
              {saving ? t("appointments.sheet.booking", "Booking…") : t("appointments.sheet.confirmBooking", "Confirm booking")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
